/**
 * video-audio-sync.js — keep a hidden <audio> element in lock-step with a
 * muted <video> element.
 *
 * Used by the dialogue-enhancer codec fallback: when the browser can't decode
 * the movie's audio codec (AC-3/E-AC-3/DTS), FFmpeg WASM transcodes ONLY the
 * audio track to an .m4a blob; the original <video> keeps playing muted and
 * this module drives the audio element after it.
 *
 *   var sync = VideoAudioSync.attach(videoEl, audioEl, {
 *       latencyOffsetSec: 0.06,  // DSP latency: audio target LEADS video by this
 *       softThreshold: 0.04,     // s — below this, leave playbackRate alone
 *       hardThreshold: 0.25,     // s — above this, hard re-seek the audio
 *       intervalMs: 500
 *   });
 *   sync.setLatencyOffset(sec);
 *   sync.detach();
 *
 * Drift correction: small drift is steered out with playbackRate nudges
 * (≤±2.5% under 100 ms, ≤±5% above — transient, inaudible as pitch), large
 * drift or a background-tab return gets a hard re-seek (m4a +faststart from
 * a blob URL seeks instantly).
 */
(function () {
    'use strict';

    function attach(video, audio, opts) {
        opts = opts || {};
        var latency = +opts.latencyOffsetSec || 0;
        var SOFT = +opts.softThreshold || 0.04;
        var HARD = +opts.hardThreshold || 0.25;
        var INTERVAL = +opts.intervalMs || 500;
        var timer = null;
        var detached = false;

        function targetTime() { return video.currentTime - latency; }
        function hardSync() {
            try { audio.currentTime = Math.max(0, targetTime()); } catch (_) {}
            audio.playbackRate = video.playbackRate;
        }

        function onPlay() { hardSync(); audio.play().catch(function () {}); }
        function onPause() { audio.pause(); }
        function onSeek() { hardSync(); }
        function onRate() { audio.playbackRate = video.playbackRate; }
        // The video element carries the user's volume/mute intent (its own
        // audio track is undecodable anyway — it plays silence); relay both.
        function onVolume() { audio.volume = video.volume; audio.muted = video.muted; }
        function onVis() { if (!document.hidden && !video.paused) hardSync(); }

        function tick() {
            if (detached || video.paused || audio.readyState < 2) return;
            var d = audio.currentTime - targetTime();
            var ad = Math.abs(d);
            if (ad > HARD) {
                hardSync();
            } else if (ad > SOFT) {
                // steer back over ~2 s: rate delta proportional to drift
                var maxDelta = ad > 0.1 ? 0.05 : 0.025;
                var delta = Math.max(-maxDelta, Math.min(maxDelta, d * 0.5));
                audio.playbackRate = video.playbackRate * (1 - delta);
            } else {
                audio.playbackRate = video.playbackRate;
            }
        }

        video.addEventListener('play', onPlay);
        video.addEventListener('pause', onPause);
        video.addEventListener('seeked', onSeek);
        video.addEventListener('ratechange', onRate);
        video.addEventListener('volumechange', onVolume);
        document.addEventListener('visibilitychange', onVis);
        timer = setInterval(tick, INTERVAL);
        onVolume();
        if (!video.paused) onPlay();

        return {
            setLatencyOffset: function (sec) { latency = +sec || 0; hardSync(); },
            drift: function () { return audio.currentTime - targetTime(); },
            detach: function () {
                detached = true;
                clearInterval(timer);
                video.removeEventListener('play', onPlay);
                video.removeEventListener('pause', onPause);
                video.removeEventListener('seeked', onSeek);
                video.removeEventListener('ratechange', onRate);
                video.removeEventListener('volumechange', onVolume);
                document.removeEventListener('visibilitychange', onVis);
                try { audio.pause(); } catch (_) {}
            }
        };
    }

    window.VideoAudioSync = { attach: attach };
})();
